import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, Trash2, AlertTriangle, CheckCircle2, Save, Banknote, CreditCard,
  Smartphone, Clock, ArrowUpDown,
} from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast, Toaster } from 'sonner';
import {
  listBranches, createBranch, deleteBranch,
  listEntries, upsertEntry, deleteEntry,
} from '../lib/api';
import { formatRp, parseNumber, formatThousands, MONTHS_ID, daysInMonth } from '../lib/format';
import { useAuth } from '../context/AuthContext';

const now = new Date();

const emptyForm = (day) => ({
  day,
  saldo_awal: '',
  tunai: '',
  pending: '',
  debit: '',
  emoney: '',
  keterangan: '',
  transaksi_system: '',
  is_balanced: true,
});

const KpiCard = ({ icon: Icon, label, value, accent = 'emerald', testid }) => {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    sky: 'bg-sky-50 text-sky-700 border-sky-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
    stone: 'bg-stone-100 text-stone-700 border-stone-200',
  };
  return (
    <div className="card-modern p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${colors[accent]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
          <div className="num text-base font-semibold text-stone-900 truncate">{value}</div>
        </div>
      </div>
    </div>
  );
};

export default function KasirPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams] = useSearchParams();

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [year, setYear] = useState(() => {
    const y = parseInt(searchParams.get('year'));
    return y && !isNaN(y) ? y : now.getFullYear();
  });
  const [month, setMonth] = useState(() => {
    const m = parseInt(searchParams.get('month'));
    return m && m >= 1 && m <= 12 ? m : now.getMonth() + 1;
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(emptyForm(1));
  const [editingId, setEditingId] = useState(null);
  const [newBranchName, setNewBranchName] = useState('');

  const dim = daysInMonth(year, month);

  const entryByDay = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => map.set(e.day, e));
    return map;
  }, [entries]);

  useEffect(() => {
    (async () => {
      const list = await listBranches();
      setBranches(list);
      const wantedBranch = searchParams.get('branch');
      if (wantedBranch && list.some((b) => b.id === wantedBranch)) {
        setBranchId(wantedBranch);
      } else if (list.length && !branchId) {
        setBranchId(list[0].id);
      }
    })();
  }, []);

  useEffect(() => {
    if (!branchId) return;
    (async () => {
      setLoading(true);
      try {
        const list = await listEntries(branchId, year, month);
        setEntries(list);
      } catch (e) {
        toast.error('Gagal memuat data');
      } finally {
        setLoading(false);
      }
    })();
  }, [branchId, year, month]);

  const openForDay = (day) => {
    const existing = entryByDay.get(day);
    if (existing) {
      setForm({
        day,
        saldo_awal: formatThousands(existing.saldo_awal || 0),
        tunai: formatThousands(existing.tunai || 0),
        pending: formatThousands(existing.pending || 0),
        debit: formatThousands(existing.debit || 0),
        emoney: formatThousands(existing.emoney || 0),
        keterangan: existing.keterangan || '',
        transaksi_system: existing.transaksi_system || 0,
        is_balanced: existing.is_balanced !== false,
      });
      setEditingId(existing.id);
    } else {
      const prev = entryByDay.get(day - 1);
      const f = emptyForm(day);
      if (prev) f.saldo_awal = formatThousands(prev.tunai || 0);
      setForm(f);
      setEditingId(null);
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!branchId) return toast.error('Pilih cabang dulu');
    const computedSelisih =
      parseNumber(form.tunai) - parseNumber(form.saldo_awal) + parseNumber(form.pending);
    try {
      await upsertEntry({
        branch_id: branchId, year, month, day: form.day,
        saldo_awal: parseNumber(form.saldo_awal),
        tunai: parseNumber(form.tunai),
        pending: parseNumber(form.pending),
        debit: parseNumber(form.debit),
        emoney: parseNumber(form.emoney),
        keterangan: form.keterangan || '',
        transaksi_system: computedSelisih,
        is_balanced: !!form.is_balanced,
      });
      toast.success(`Tanggal ${form.day} tersimpan`);
      const list = await listEntries(branchId, year, month);
      setEntries(list);
      setOpenDialog(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Gagal menyimpan');
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      await deleteEntry(editingId);
      toast.success('Data dihapus');
      const list = await listEntries(branchId, year, month);
      setEntries(list);
      setOpenDialog(false);
    } catch (e) {
      toast.error('Gagal menghapus');
    }
  };

  const handleAddBranch = async () => {
    if (!newBranchName.trim()) return;
    try {
      const b = await createBranch(newBranchName.trim());
      const list = await listBranches();
      setBranches(list);
      setBranchId(b.id);
      setNewBranchName('');
      toast.success(`Cabang ${b.name} ditambahkan`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Gagal menambah cabang');
    }
  };

  const handleDeleteBranch = async () => {
    if (!branchId) return;
    if (!window.confirm('Hapus cabang ini beserta seluruh datanya?')) return;
    try {
      await deleteBranch(branchId);
      const list = await listBranches();
      setBranches(list);
      setBranchId(list[0]?.id || '');
      toast.success('Cabang dihapus');
    } catch (e) {
      toast.error('Gagal menghapus cabang');
    }
  };

  const totals = useMemo(() => {
    const acc = { tunai: 0, debit: 0, emoney: 0, pending: 0, selisih: 0, unbalanced: 0, days: entries.length };
    entries.forEach((e) => {
      acc.tunai += e.tunai || 0;
      acc.debit += e.debit || 0;
      acc.emoney += e.emoney || 0;
      acc.pending += e.pending || 0;
      acc.selisih += e.transaksi_system || 0;
      if (e.is_balanced === false) acc.unbalanced += 1;
    });
    return acc;
  }, [entries]);

  const days = Array.from({ length: dim }, (_, i) => i + 1);
  const branchName = branches.find((b) => b.id === branchId)?.name || '';

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster position="top-right" richColors />
      <AppHeader />

      <main className="max-w-[1400px] mx-auto px-6 md:px-8 py-8 animate-fade-in">
        {/* Page header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
              Input Harian
            </div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight">
              Buku Kas · <span className="text-emerald-800">{branchName || '—'}</span>
            </h1>
            <p className="text-sm text-stone-500 mt-1 num">
              {MONTHS_ID[month - 1]} {year} · {totals.days}/{dim} hari terisi
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <Input
                  placeholder="Nama cabang baru"
                  value={newBranchName}
                  onChange={(e) => setNewBranchName(e.target.value)}
                  className="bg-white w-48"
                  data-testid="new-branch-input"
                />
                <Button
                  onClick={handleAddBranch}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white"
                  data-testid="add-branch-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Cabang
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="card-modern p-5 mb-6" data-testid="filters-bar">
          <div className="grid md:grid-cols-3 gap-4 items-end">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Cabang
              </Label>
              <div className="flex gap-2">
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger data-testid="branch-select" className="bg-white">
                    <SelectValue placeholder="Pilih cabang" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`branch-option-${b.name}`}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleDeleteBranch}
                    data-testid="delete-branch-btn"
                    title="Hapus cabang ini"
                    disabled={!branchId}
                  >
                    <Trash2 className="w-4 h-4 text-red-700" />
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Bulan
              </Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger data-testid="month-select" className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_ID.map((m, idx) => (
                    <SelectItem
                      key={m}
                      value={String(idx + 1)}
                      data-testid={`month-option-${idx + 1}`}
                    >
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                Tahun
              </Label>
              <Input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value) || now.getFullYear())}
                className="bg-white"
                data-testid="year-input"
              />
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6 stagger">
          <KpiCard icon={Banknote} label="Total Tunai" value={formatRp(totals.tunai)} accent="emerald" testid="kpi-tunai" />
          <KpiCard icon={CreditCard} label="Total Debit" value={formatRp(totals.debit)} accent="sky" testid="kpi-debit" />
          <KpiCard icon={Smartphone} label="Total E-Money" value={formatRp(totals.emoney)} accent="amber" testid="kpi-emoney" />
          <KpiCard icon={Clock} label="Total Pending" value={formatRp(totals.pending)} accent="stone" testid="kpi-pending" />
          <KpiCard icon={ArrowUpDown} label="Transaksi System (Tunai)" value={formatRp(totals.selisih)} accent={totals.selisih < 0 ? 'rose' : 'emerald'} testid="kpi-selisih" />
        </div>

        {totals.unbalanced > 0 && (
          <div
            className="mb-4 flex items-center gap-3 bg-red-50 border-l-4 border-red-600 px-4 py-3 rounded-r-md animate-rise"
            data-testid="unbalanced-alert"
          >
            <AlertTriangle className="w-5 h-5 text-red-700" />
            <div className="text-sm text-red-900">
              <span className="font-semibold">{totals.unbalanced} baris tidak balance</span> — mohon periksa kembali entry berikut.
            </div>
          </div>
        )}

        {/* Ledger table */}
        <div className="card-modern overflow-hidden" data-testid="ledger-container">
          <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500">
                Buku Kas — {branchName}
              </div>
              <h2 className="font-heading text-xl font-semibold text-stone-900">
                {MONTHS_ID[month - 1]} {year}
              </h2>
            </div>
            {loading && <div className="text-xs text-stone-500 animate-pulse">Memuat…</div>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full ledger" data-testid="ledger-table">
              <thead>
                <tr>
                  <th style={{ width: '4%' }}>Tgl</th>
                  <th style={{ width: '11%' }} className="text-right">Saldo Awal</th>
                  <th style={{ width: '11%' }} className="text-right">Tunai</th>
                  <th style={{ width: '10%' }} className="text-right">Pending</th>
                  <th style={{ width: '11%' }} className="text-right">Debit</th>
                  <th style={{ width: '10%' }} className="text-right">E-Money</th>
                  <th>Keterangan</th>
                  <th style={{ width: '11%' }} className="text-right">Transaksi System</th>
                  <th style={{ width: '10%' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => {
                  const e = entryByDay.get(d);
                  const unbalanced = e && e.is_balanced === false;
                  return (
                    <tr
                      key={d}
                      className={`${unbalanced ? 'row-unbalanced' : ''} cursor-pointer`}
                      onClick={() => openForDay(d)}
                      data-testid={`row-day-${d}`}
                    >
                      <td className="num font-semibold">{d}</td>
                      <td className="num-cell">{e ? formatRp(e.saldo_awal) : <span className="text-stone-300">—</span>}</td>
                      <td className="num-cell">{e ? formatRp(e.tunai) : <span className="text-stone-300">—</span>}</td>
                      <td className="num-cell">{e ? formatRp(e.pending) : <span className="text-stone-300">—</span>}</td>
                      <td className="num-cell">{e ? formatRp(e.debit) : <span className="text-stone-300">—</span>}</td>
                      <td className="num-cell">{e ? formatRp(e.emoney) : <span className="text-stone-300">—</span>}</td>
                      <td className="text-sm text-stone-700">
                        {e?.keterangan
                          ? <span className="line-clamp-1">{e.keterangan}</span>
                          : <span className="text-stone-300">—</span>}
                        {unbalanced && (
                          <span className="ml-2 text-[11px] font-bold text-red-800 uppercase tracking-wider">
                            Tidak Balance — Tolong Dicek
                          </span>
                        )}
                      </td>
                      <td className="num-cell">
                        {e ? (
                          <span className={e.transaksi_system < 0 ? 'text-red-700' : 'text-stone-900'}>
                            {formatRp(e.transaksi_system)}
                          </span>
                        ) : <span className="text-stone-300">—</span>}
                      </td>
                      <td>
                        {e ? (
                          unbalanced ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-900 bg-red-100 border border-red-300 px-2 py-1 rounded">
                              <AlertTriangle className="w-3 h-3" /> TIDAK BALANCE
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                              <CheckCircle2 className="w-3 h-3" /> Balance
                            </span>
                          )
                        ) : (
                          <span className="text-[11px] text-stone-400">Belum diisi</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Entry Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl" data-testid="entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Setoran — {branchName} · {form.day} {MONTHS_ID[month - 1]} {year}
            </DialogTitle>
            <DialogDescription>
              Isi nilai saat buka & tutup toko. Nilai kosong dianggap 0.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            {[
              ['saldo_awal', 'Saldo Awal (Buka)'],
              ['tunai', 'Tunai (Tutup)'],
              ['pending', 'Pending'],
              ['debit', 'Debit'],
              ['emoney', 'E-Money'],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
                  {label}
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: formatThousands(e.target.value) })}
                  className="font-mono bg-white"
                  data-testid={`input-${k}`}
                  placeholder="0"
                />
              </div>
            ))}
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block flex items-center gap-1">
                Transaksi System <span className="text-emerald-700 normal-case font-normal">(otomatis)</span>
              </Label>
              <div
                className="font-mono bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 h-10 flex items-center text-emerald-900 font-semibold"
                data-testid="input-transaksi_system"
              >
                {formatRp(
                  parseNumber(form.tunai) - parseNumber(form.saldo_awal) + parseNumber(form.pending)
                )}
              </div>
              <div className="text-[10px] text-stone-500 mt-1 font-mono">
                Tunai − Saldo Awal + Pending
              </div>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
              Keterangan
            </Label>
            <Textarea
              value={form.keterangan}
              onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
              placeholder="Contoh: BELI GALON 23.000, LUNAS QRIS 4616 (145.000)"
              className="bg-white"
              data-testid="input-keterangan"
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-md p-3">
            <div>
              <div className="font-heading font-medium text-sm text-stone-900">Status Balance</div>
              <div className="text-xs text-stone-600">Matikan jika kas tidak balance dan perlu dicek.</div>
            </div>
            <Switch
              checked={form.is_balanced}
              onCheckedChange={(v) => setForm({ ...form, is_balanced: v })}
              data-testid="switch-balance"
            />
          </div>

          <DialogFooter className="flex justify-between gap-2">
            {editingId ? (
              <Button
                variant="outline"
                onClick={handleDelete}
                className="border-red-200 text-red-700 hover:bg-red-50"
                data-testid="delete-entry-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Hapus
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpenDialog(false)} data-testid="cancel-entry-btn">
                Batal
              </Button>
              <Button
                onClick={handleSave}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="save-entry-btn"
              >
                <Save className="w-4 h-4 mr-1" /> Simpan
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
